<template>
  <transition name="drawer" appear>
    <div v-if="visible" class="mp-drawer">
      <!-- 独立背景遮罩 -->
      <div class="mp-backdrop" @click="closeSearch"></div>

      <!-- 内容面板 -->
      <div class="mp-panel">
        <div class="mp-wrap">
          <img
            src="@/assets/mobile/home/logo_black.png"
            alt=""
            class="mp-search-logo"
            @click="goHome"
            style="padding-top: 5rem;"
          />
          <div class="mp-header">
            <div class="mp-search">
              <input
                class="mp-search-input"
                type="search"
                v-model="searchQuery"
                @blur="judgeSearchInfo"
                placeholder="What are you looking for?"
                @keyup.enter="onSearch"
                enterkeyhint="search"
              />
            </div>
            <p v-if="showResults" class="mp-search-count">Found {{ searchTotal }} results</p>
          </div>

          <div class="mp-content" @click="closeSearch">
            <div v-if="searchLoading && searchPageIndex === 1" class="loading-indicator">Loading...</div>

            <div v-else-if="showResults" class="mp-grid">
              <div
                v-for="(item, idx) in searchGridItems"
                :key="item.ProductID || 'placeholder-' + idx"
                class="mp-card"
                @click="!item.isPlaceholder && openItem(item)"
              >
                <template v-if="!item.isPlaceholder">
                  <ProgressiveImage :src="item.image || defaultImage" :alt="item.name" customClass="mp-thumb" />
                </template>
                <template v-else>
                  <div class="mp-thumb placeholder-thumb"></div>
                </template>
              </div>
            </div>

            <div v-else ref="sentinel" class="mp-sentinel"></div>

            <div v-if="searchLoading && searchPageIndex > 1" class="loading-more">Loading more...</div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import ProgressiveImage from '@/components/ProgressiveImage.vue'
import { searchProducts } from '@/api/product'
import { showMessage } from '@/config/message'

export default {
  name: 'MobileSearch',
  components: { ProgressiveImage },
  props: {
    section: { type: String, default: '' },
    visible: { type: Boolean, default: false }
  },
  data() {
    return {
      searchQuery: '',
      showResults: false,
      searchLoading: false,
      searchPageIndex: 1,
      searchTotal: 0,
      searchHasMore: true,
      searchResults: [],
      defaultImage: '',
      observer: null,
      allItems: [],
      visibleItems: [],
      page: 0,
      pageSize: 4
    }
  },
  computed: {
    searchGridItems() {
      if (!this.showResults) return []
      const items = this.searchResults
      if (items.length % 2 === 0) return items
      return [...items, { isPlaceholder: true }]
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        document.body.style.overflow = 'hidden'
        this.resetAndLoad()
        this.initObserver()
        history.pushState({ searchOpen: true }, '', location.href)
      } else {
        document.body.style.overflow = ''
        if (this.observer) {
          this.observer.disconnect()
          this.observer = null
        }
        history.replaceState(null, '', location.href)
      }
    }
  },
  mounted() {
    if (this.visible) {
      document.body.style.overflow = 'hidden'
      this.resetAndLoad()
      this.initObserver()
      history.pushState({ searchOpen: true }, '', location.href)
    }
    window.addEventListener('popstate', this.handlePopState)
  },
  beforeDestroy() {
    document.body.style.overflow = ''
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
    window.removeEventListener('popstate', this.handlePopState)
  },
  methods: {
    handlePopState(event) {
      if (this.visible) {
        this.closeSearch()
      }
    },
    goHome() {
      this.closeSearch()
      setTimeout(() => this.$router.push('/m'), 300)
    },
    closeSearch() {
      this.$emit('update:visible', false)
      this.$emit('close')
    },
    judgeSearchInfo() {
      if (!this.searchQuery.trim()) {
        this.showResults = false
        this.searchResults = []
        this.searchPageIndex = 1
        this.searchTotal = 0
      }
    },
    async onSearch() {
      const keyword = this.searchQuery.trim()
      if (!keyword) {
        showMessage('Please enter a keyword', 'error')
        return
      }
      this.showResults = true
      this.searchResults = []
      this.searchPageIndex = 1
      this.searchHasMore = true
      this.searchLoading = true
      await this.fetchSearchResults(keyword, 1)
    },
    async fetchSearchResults(keyword, pageIndex) {
      try {
        const response = await searchProducts({ keyword, pageIndex, pageSize: 20 })
        if (response.retCode === 1) {
          const products = response.result || []
          const total = response.totalCount || 0
          const formatted = products.map(p => ({
            ProductID: p.ProductID,
            name: p.ProductName,
            image: p.Images.find(img => img.IsPrimary).ImageURL || p.Images[0].ImageURL || ''
          }))
          if (pageIndex === 1) {
            this.searchResults = formatted
          } else {
            this.searchResults = [...this.searchResults, ...formatted]
          }
          this.searchTotal = total
          this.searchHasMore = this.searchResults.length < total
          this.searchPageIndex = pageIndex
        } else {
          showMessage(response.retMsg || 'Search failed', 'error')
        }
      } catch (error) {
        console.error('Search error:', error)
        showMessage('Network error, please try again', 'error')
      } finally {
        this.searchLoading = false
      }
    },
    async loadMore() {
      if (!this.showResults || this.searchLoading || !this.searchHasMore) return
      const nextPage = this.searchPageIndex + 1
      await this.fetchSearchResults(this.searchQuery.trim(), nextPage)
    },
    onIntersect(entries) {
      if (this.showResults) return
      const entry = entries[0]
      if (entry && entry.isIntersecting) {
        this.loadDefaultMore()
      }
    },
    loadDefaultMore() {
      if (this.page * this.pageSize >= this.allItems.length) return
      const start = this.page * this.pageSize
      const end = start + this.pageSize
      this.visibleItems = this.visibleItems.concat(this.allItems.slice(start, end))
      this.page += 1
    },
    buildAllItems() {
      let base = []
      if (this.section === 'seating') {
        base = [
          { img: require('@/assets/mobile/product/Rectangle 81.png'), title: 'Chair' },
          { img: require('@/assets/mobile/product/Rectangle 85.png'), title: 'Stool' }
        ]
      } else if (this.section === 'accessories') {
        base = [
          { img: require('@/assets/mobile/product/TGT-Melissa-Marshall-27 2.png'), title: 'Planter' },
          { img: require('@/assets/mobile/product/Rectangle 82.png'), title: 'Lighting' },
          { img: require('@/assets/mobile/product/Rectangle 81.png'), title: 'Mirror' },
          { img: require('@/assets/mobile/product/Rectangle 85.png'), title: 'Hardware' }
        ]
      } else {
        base = [
          { img: require('@/assets/mobile/product/TGT-Melissa-Marshall-27 2.png'), title: 'Entry Table' },
          { img: require('@/assets/mobile/product/Rectangle 82.png'), title: 'Console Table' },
          { img: require('@/assets/mobile/product/Rectangle 81.png'), title: 'Coffee Table' },
          { img: require('@/assets/mobile/product/Rectangle 85.png'), title: 'Side Table' }
        ]
      }
      const times = 5
      this.allItems = []
      for (let i = 0; i < times; i++) {
        this.allItems = this.allItems.concat(base.map(b => ({ ...b })))
      }
    },
    resetAndLoad() {
      this.page = 0
      this.visibleItems = []
      this.buildAllItems()
      this.loadDefaultMore()
    },
    initObserver() {
      if (this.observer) this.observer.disconnect()
      const root = this.$el.querySelector('.mp-content') || null
      this.observer = new IntersectionObserver(this.onIntersect, {
        root,
        threshold: 0.1
      })
      if (this.$refs.sentinel) {
        this.observer.observe(this.$refs.sentinel)
      }
    },
    openItem(item) {
      this.$router.push(`/m/product/${item.ProductID || 17}`)
      this.closeSearch()
    }
  }
}
</script>

<style scoped>
/* ===== 过渡动画：整体淡入淡出 ===== */
.drawer-enter-active,
.drawer-leave-active {
  transition: opacity 0.4s ease;
}
.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
}
.drawer-enter-to,
.drawer-leave-from {
  opacity: 1;
}

/* ===== 整体容器 ===== */
.mp-drawer {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
}

/* ===== 背景遮罩（独立过渡） ===== */
.mp-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(0px);
  -webkit-backdrop-filter: blur(0px);
  transition: backdrop-filter 0.5s ease, background 0.5s ease;
  z-index: 0;
  cursor: pointer;
}
/* 进入后应用模糊（配合 v-if 进入时直接应用） */
.mp-drawer .mp-backdrop {
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.5);
}

/* ===== 内容面板 ===== */
.mp-panel {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
}

/* 其余样式完全保留 */
.mp-wrap {
  display: flex;
  flex-direction: column;
  font-family: 'Apercu', sans-serif;
  min-height: 0;
  height: 100%;
  width: 100%;
}

.mp-header {
  position: sticky;
  z-index: 2;
  background: rgba(255, 255, 255, 0.9);
  padding: 97rem 68rem 28rem;
  border-bottom: 1rem solid #000;
}

.mp-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-y: contain !important;
}

.mp-search {
  display: flex;
  align-items: center;
  gap: 12rem;
  border-bottom: 1rem solid #000;
}

.mp-search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 16px;
  font-weight: 400;
  color: #000;
  padding: 0 0 3rem;
  background: transparent;
  font-family: 'Apercu-Medium', sans-serif;
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;
}

.mp-search-input::placeholder {
  color: #999;
}

.mp-search-input:focus {
  font-size: 16px;
}

.mp-search-count {
  font-size: 12rem;
  color: #999;
  margin: 10rem 0 10rem;
}

.mp-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-left: 0.25rem solid #000;
}

.mp-card {
  display: flex;
  flex-direction: column;
  border-right: 0.25rem solid #000;
  border-bottom: 0.25rem solid #000;
  box-sizing: border-box;
  min-height: 245rem;
}

.mp-thumb {
  display: block;
  width: 100%;
  height: 245rem;
  object-fit: cover;
}

.placeholder-thumb {
  width: 100%;
  height: 245rem;
  background: transparent;
}

.mp-sentinel {
  width: 100%;
  height: 1rem;
}

.mp-search-logo {
  background: #fff;
  z-index: 999;
  cursor: pointer;
}

.loading-indicator,
.loading-more {
  text-align: center;
  padding: 20rem;
  font-size: 14rem;
  color: #666;
}
</style>